import { motion } from "framer-motion";
import { MessageSquare, Phone, Send, CalendarDays, MapPin } from "lucide-react";
import { BRAND, CONTACT, IMAGES } from "@/data/site";
import { smsHref, telHref, mailHref, scrollTo } from "@/lib/actions";

const footerLinks = [
  { label: "Services", href: "#services", testid: "footer-services" },
  { label: "Awards", href: "#awards", testid: "footer-awards" },
  { label: "About Malcolm", href: "#about", testid: "footer-about" },
  { label: "Reviews", href: "#reviews", testid: "footer-reviews" },
  { label: "Contact", href: "#contact", testid: "footer-contact" },
];

export const FinalCTA = () => {
  return (
    <>
      <section data-testid="final-cta-section" className="relative py-28 lg:py-36 overflow-hidden bg-[#0A0A0A]">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMAGES.finalCtaBg})` }} />
        <div className="absolute inset-0 bg-black/80" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight uppercase text-white leading-[0.95]">
            Ready to take the <span className="text-[#C8102E]">next step?</span>
          </h2>
          <p className="mt-5 text-lg text-gray-300">Malcolm is here to help you every step of the way.</p>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <a href={smsHref} data-testid="final-text-btn" className="inline-flex items-center gap-2 px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-white bg-[#C8102E] rounded-sm hover:bg-[#A00D25] transition-all hover:-translate-y-0.5 shadow-lg shadow-[#C8102E]/30">
              <MessageSquare size={18} /> Text Malcolm
            </a>
            <a href={telHref} data-testid="final-call-btn" className="inline-flex items-center gap-2 px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-white border border-white/25 rounded-sm hover:bg-white/10 transition-all hover:-translate-y-0.5">
              <Phone size={18} /> Call Malcolm
            </a>
            <button onClick={() => scrollTo("#contact")} data-testid="final-request-btn" className="inline-flex items-center gap-2 px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-white border border-white/25 rounded-sm hover:bg-white/10 transition-all hover:-translate-y-0.5">
              <Send size={18} /> Send a Request
            </button>
            <button onClick={() => scrollTo("#contact")} data-testid="final-schedule-btn" className="inline-flex items-center gap-2 px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-white border border-white/25 rounded-sm hover:bg-white/10 transition-all hover:-translate-y-0.5">
              <CalendarDays size={18} /> Schedule Appointment
            </button>
          </div>
        </motion.div>
      </section>

      <footer data-testid="site-footer" className="bg-black border-t border-white/10 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div>
              <img src={BRAND.logo} alt="Cars With Malcolm" className="h-14 w-auto object-contain" />
              <p className="mt-4 text-sm text-gray-400 leading-relaxed">
                {BRAND.tagline}<br />
                {BRAND.person}
              </p>
              <p className="mt-4 flex items-start gap-2 text-sm text-gray-400">
                <MapPin size={16} className="text-[#C8102E] mt-0.5 shrink-0" /> {BRAND.serving}
              </p>
            </div>

            <div>
              <h4 className="font-heading text-sm font-bold uppercase tracking-wide text-white">Quick Links</h4>
              <ul className="mt-4 space-y-3 text-sm">
                {footerLinks.map((l) => (
                  <li key={l.href}>
                    <a
                      href={l.href}
                      data-testid={l.testid}
                      onClick={(e) => { e.preventDefault(); scrollTo(l.href); }}
                      className="text-gray-400 hover:text-[#C8102E] transition-colors"
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-heading text-sm font-bold uppercase tracking-wide text-white">Get In Touch</h4>
              <ul className="mt-4 space-y-3 text-sm">
                <li><a href={telHref} data-testid="footer-phone" className="text-gray-400 hover:text-[#C8102E] transition-colors">{CONTACT.phone}</a></li>
                <li><a href={mailHref} data-testid="footer-email" className="text-gray-400 hover:text-[#C8102E] transition-colors">{CONTACT.email}</a></li>
                <li><span className="text-gray-400">{BRAND.website}</span></li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-white/10">
            <p className="text-xs text-gray-500 leading-relaxed">
              Cars With Malcolm is a personal automotive liaison page for Malcolm Burke. Vehicle availability, pricing, financing terms, approvals, incentives, and details are subject to change and must be confirmed with the appropriate dealership, lender, or provider. Financing is subject to lender approval.
            </p>
            <p className="mt-4 text-xs text-gray-600">© {new Date().getFullYear()} Cars With Malcolm. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </>
  );
};
